import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  IconButton,
  ToggleButtonGroup,
  Button,
  Typography,
  CircularProgress,
} from '@mui/joy';
import {
  DeviceMobile as MobileIcon,
  DeviceTablet as TabletIcon,
  Desktop as DesktopIcon,
  ArrowsClockwise as RefreshIcon,
  ArrowsOut as FullscreenIcon,
  ArrowSquareOut as OpenNewIcon,
} from '@phosphor-icons/react';
import api from '../../lib/api';

interface LivePreviewProps {
  siteId: number | string;
  pageData: {
    slug: string;
    sections_draft: any[];
    seo_draft: any;
    template_key: string;
  };
  siteData: {
    domain: string;
    theme_key: string;
    settings: any;
  };
  refreshTrigger?: number;
}

type DeviceMode = 'mobile' | 'tablet' | 'desktop';

const DEVICE_SIZES = {
  mobile: { width: 375, height: 667, label: 'iPhone SE' },
  tablet: { width: 768, height: 1024, label: 'iPad' },
  desktop: { width: 1366, height: 768, label: 'Desktop' },
};

export const LivePreview: React.FC<LivePreviewProps> = ({
  siteId,
  pageData,
  siteData,
  refreshTrigger = 0,
}) => {
  const [deviceMode, setDeviceMode] = useState<DeviceMode>('desktop');
  const [isLoading, setIsLoading] = useState(true);
  const [scale, setScale] = useState(1);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string>('');
  const [urlLoading, setUrlLoading] = useState(true);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Extract origin from URL for postMessage security
  const getOriginFromUrl = (url: string): string => {
    try {
      return new URL(url).origin;
    } catch {
      return window.location.origin; // Fallback to current origin
    }
  };

  // Fetch signed preview URL from backend
  useEffect(() => {
    const fetchPreviewUrl = async () => {
      setUrlLoading(true);
      try {
        const slug = pageData.slug === 'index' ? 'index' : pageData.slug;
        const response = await api.get<{ preview_url: string }>(`/api/cms/sites/${siteId}/generate-preview-url/?slug=${encodeURIComponent(slug)}`);
        setPreviewUrl(response.data.preview_url);
      } catch (error) {
        console.error('Failed to generate preview URL:', error);
        // Set a fallback URL that will show an error
        setPreviewUrl(`/cms/sites/preview/${pageData.slug === 'index' ? '' : pageData.slug}`);
      } finally {
        setUrlLoading(false);
      }
    };

    fetchPreviewUrl();
  }, [siteId, pageData.slug]);

  // Calculate scale to fit preview in container
  useEffect(() => {
    const calculateScale = () => {
      if (!containerRef.current) return;

      const container = containerRef.current;
      const containerWidth = container.clientWidth - 40; // Padding
      const containerHeight = container.clientHeight - 100; // Header space

      const deviceSize = DEVICE_SIZES[deviceMode];
      const scaleX = containerWidth / deviceSize.width;
      const scaleY = containerHeight / deviceSize.height;

      setScale(Math.min(scaleX, scaleY, 1));
    };

    calculateScale();
    window.addEventListener('resize', calculateScale);
    return () => window.removeEventListener('resize', calculateScale);
  }, [deviceMode, isFullscreen]);

  // Refresh iframe when content changes
  useEffect(() => {
    if (iframeRef.current && refreshTrigger > 0) {
      setIsLoading(true);
      // Post message to iframe to update content with origin validation
      const targetOrigin = previewUrl ? getOriginFromUrl(previewUrl) : window.location.origin;
      iframeRef.current.contentWindow?.postMessage({
        type: 'UPDATE_PREVIEW',
        data: {
          sections: pageData.sections_draft,
          seo: pageData.seo_draft,
          settings: siteData.settings,
        }
      }, targetOrigin);
    }
  }, [refreshTrigger, pageData, siteData, previewUrl]);

  const handleRefresh = async () => {
    if (iframeRef.current) {
      setIsLoading(true);
      // Generate a fresh signed URL before refreshing
      try {
        const slug = pageData.slug === 'index' ? 'index' : pageData.slug;
        const response = await api.get<{ preview_url: string }>(`/api/cms/sites/${siteId}/generate-preview-url/?slug=${encodeURIComponent(slug)}`);
        const newUrl = response.data.preview_url;
        setPreviewUrl(newUrl);
        iframeRef.current.src = newUrl;
      } catch (error) {
        console.error('Failed to refresh preview:', error);
        iframeRef.current.src = iframeRef.current.src;
      }
    }
  };

  const handleFullscreen = () => {
    if (!document.fullscreenElement) {
      containerRef.current?.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  const handleOpenInNew = async () => {
    try {
      const slug = pageData.slug === 'index' ? 'index' : pageData.slug;
      const response = await api.get<{ preview_url: string }>(`/api/cms/sites/${siteId}/generate-preview-url/?slug=${encodeURIComponent(slug)}`);
      window.open(response.data.preview_url, '_blank');
    } catch (error) {
      console.error('Failed to open preview:', error);
      window.open(previewUrl, '_blank');
    }
  };

  const deviceSize = DEVICE_SIZES[deviceMode];

  return (
    <Box
      ref={containerRef}
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'background.level1',
        borderRadius: 'md',
        overflow: 'hidden',
      }}
    >
      {/* Preview Header */}
      <Box
        sx={{
          p: 2,
          borderBottom: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          bgcolor: 'background.surface',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography level="title-md">Live Preview</Typography>
          {deviceMode !== 'desktop' && (
            <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
              {deviceSize.label} ({deviceSize.width} × {deviceSize.height})
            </Typography>
          )}
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ToggleButtonGroup
            value={deviceMode}
            onChange={(event, value) => value && setDeviceMode(value)}
            size="sm"
          >
            <Button value="mobile" startDecorator={<MobileIcon />}>
              Mobile
            </Button>
            <Button value="tablet" startDecorator={<TabletIcon />}>
              Tablet
            </Button>
            <Button value="desktop" startDecorator={<DesktopIcon />}>
              Desktop
            </Button>
          </ToggleButtonGroup>

          <IconButton
            size="sm"
            variant="outlined"
            onClick={handleRefresh}
            disabled={isLoading}
          >
            <RefreshIcon />
          </IconButton>

          <IconButton
            size="sm"
            variant="outlined"
            onClick={handleFullscreen}
          >
            <FullscreenIcon />
          </IconButton>

          <IconButton
            size="sm"
            variant="outlined"
            onClick={handleOpenInNew}
          >
            <OpenNewIcon />
          </IconButton>
        </Box>
      </Box>

      {/* Preview Container */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 2,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {(isLoading || urlLoading) && (
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 10,
              bgcolor: 'background.surface',
              borderRadius: 'md',
              p: 3,
              boxShadow: 'lg',
            }}
          >
            <CircularProgress />
            <Typography level="body-sm" sx={{ mt: 2 }}>
              Loading preview...
            </Typography>
          </Box>
        )}

        {/* Device Frame */}
        <Box
          sx={{
            width: deviceSize.width,
            height: deviceSize.height,
            transform: `scale(${scale})`,
            transformOrigin: 'center',
            transition: 'all 0.3s ease',
            bgcolor: 'background.surface',
            borderRadius: deviceMode === 'mobile' ? '2rem' : '0.5rem',
            boxShadow: 'xl',
            border: deviceMode !== 'desktop' ? '8px solid' : 'none',
            borderColor: 'neutral.800',
            overflow: 'hidden',
            position: 'relative',
          }}
        >
          {/* Notch for mobile */}
          {deviceMode === 'mobile' && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: '50%',
                transform: 'translateX(-50%)',
                width: 150,
                height: 25,
                bgcolor: 'neutral.800',
                borderRadius: '0 0 1rem 1rem',
                zIndex: 1,
              }}
            />
          )}

          {/* Iframe */}
          {previewUrl && (
            <iframe
              ref={iframeRef}
              src={previewUrl}
              style={{
                width: '100%',
                height: '100%',
                border: 'none',
                backgroundColor: 'white',
              }}
              onLoad={() => setIsLoading(false)}
              title="Page Preview"
              sandbox="allow-scripts allow-same-origin allow-forms"
            />
          )}
        </Box>
      </Box>
    </Box>
  );
};
